import classNames from 'classnames';
import type { JSX, MouseEvent, ReactNode } from 'react';
import './Switch.css';
import type { IconGlyph } from '@ipfabric/ui-components/Icon/IconGlyph';

export type SwitchOptionType<TValue> = {
  label: ReactNode;
  value: TValue;
  icon?: IconGlyph;
  dataTestId?: string;
};

/**
 * Function to get comparable string from value
 */
export type GetSwitchOptionValueFunction<TValue> = (
  value: TValue | null,
) => string;

export type SwitchProps<TValue> = {
  className?: string;
  disabled?: boolean;
  options: SwitchOptionType<TValue>[];
  value: TValue | null;
  hasError?: boolean;
  onChange: (value: TValue) => void;
  size?: 'normal' | 'small' | 'mini';
} & (SwitchOptionType<TValue> extends { value: string }
  ? { getOptionValue?: GetSwitchOptionValueFunction<TValue> }
  : { getOptionValue: GetSwitchOptionValueFunction<TValue> });

export const getSwitchOptionValueDefault = (
  value: string | null,
  // using uuid for null value in case null was an actual
  // option value. This can happen if there is need for
  // explicitly selectable empty value
): string => value ?? 'f489f5e3-3374-4276-8de5-d2be37ce4a28';

/**
 * Switch component intended for use in form.
 * Note: component not intended to grow full width should have
 * width constrained from outside to match widest option.
 */
export const Switch = <TValue,>({
  getOptionValue = getSwitchOptionValueDefault as GetSwitchOptionValueFunction<TValue>,
  ...props
}: SwitchProps<TValue>): JSX.Element => {
  const makeHandleClick = (value: TValue) => (e: MouseEvent) => {
    e.preventDefault();

    props.onChange(value);
  };

  return (
    <div
      className={classNames(
        'switch',
        `switch--${props.size ?? 'normal'}`,
        props.hasError && props.value !== null && 'switch--value-error',
        props.hasError && props.value === null && 'switch--novalue-error',
        props.className,
      )}
    >
      {props.options.map((option) => (
        <button
          key={getOptionValue(option.value)}
          disabled={props.disabled}
          onClick={makeHandleClick(option.value)}
          className={classNames(
            'switch__option',
            getOptionValue(option.value) === getOptionValue(props.value) &&
              'switch__option--selected',
            props.disabled && 'switch__option--disabled',
          )}
          type='button'
          data-testid={option.dataTestId}
        >
          {option.icon && (
            <i
              className={classNames(
                'fa fa-regular fa-fw switch__fa',
                option.icon,
              )}
            />
          )}
          {option.label}
        </button>
      ))}
    </div>
  );
};
